import React, { useEffect, useState } from "react";
import {
  AddIcon,
  BorderColorIcon,
  CreateNewFolderIcon,
  FilterAltOutlinedIcon,
} from "../../components/icons/Icons";
import "./StockTable.css";
import SearchComponent from "../../components/search/SearchComp";
import FilterStockModal from "./FilterStockModal";
import StockAddModal from "./StockAddModal";
import axiosInstance from "../../axiosInstance";
import LoadingComponent from "../../components/loading/Loading";
import EditStockModal from "./EditStockModal";
import { toast } from "react-toastify";
import DeleteConfirmModal from "../../components/delete/DeleteConfirmModal";
import NoItemFound from "../../components/NoItem/NoItem";

interface Segment {
  id: string;
  name: string;
}

interface Owner {
  id: string;
  full_name: string;
}

interface Product {
  id: string;
  createdAt: string;
  name: string;
  stock_count: number;
  amount: number;
  description: string;
  image: string;
  segment: Segment;
  owner: Owner;
}

const StockTable: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [isFilterModalOpen, setFilterModalOpen] = useState(false);
  const [isStockModalOpen, setStockModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeStock, setActiveStock] = useState<Product | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [stockToDelete, setStockToDelete] = useState<Product | null>(null);

  // Fetch products with server-side pagination
  const fetchProducts = async (page = currentPage, limit = entriesPerPage) => {
    try {
      setLoading(true);

      const res = await axiosInstance.get(
        `/products/me?page=${page}&limit=${limit}`
      );

      if (res?.data?.valid) {
        const data = res.data.data;

        const sorted = (data.products || []).sort(
          (a: Product, b: Product) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setProducts(sorted);
        setFilteredProducts(sorted);
        setTotalProducts(data.pagination?.total || 0);
      } else {
        setProducts([]);
        setFilteredProducts([]);
        setTotalProducts(0);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
      setFilteredProducts([]);
      setTotalProducts(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts(currentPage, entriesPerPage);
  }, [currentPage, entriesPerPage]);

  const handleFilter = (filters: {
    name: string;
    category: string;
    lis: string;
    priceMin: string;
    priceMax: string;
  }) => {
    let filtered = products;

    if (filters.name) {
      filtered = filtered.filter((p) =>
        p.name.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    if (filters.category) {
      filtered = filtered.filter((p) =>
        p.segment?.name.toLowerCase().includes(filters.category.toLowerCase())
      );
    }

    if (filters.lis) {
      filtered = filtered.filter(
        (p) => p.stock_count === Number(filters.lis)
      );
    }

    if (filters.priceMin) {
      filtered = filtered.filter((p) => p.amount >= Number(filters.priceMin));
    }

    if (filters.priceMax) {
      filtered = filtered.filter((p) => p.amount <= Number(filters.priceMax));
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  };

  const handleSearch = (query: string) => {
    if (!query) return setFilteredProducts(products);

    const q = query.toLowerCase();

    setFilteredProducts(
      products.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.segment?.name.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q)
      )
    );
  };

  const totalPages = Math.ceil(totalProducts / entriesPerPage);

  const handlePageChange = (pageNumber: number) => {
    if (pageNumber > 0 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleEntriesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEntriesPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const confirmDelete = async () => {
    if (!stockToDelete) return;

    try {
      await axiosInstance.delete(`/products?product_id=${stockToDelete.id}`);
      toast.success("Product deleted successfully!");
      fetchProducts(currentPage, entriesPerPage);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Failed to delete product");
    } finally {
      setDeleteModalOpen(false);
      setStockToDelete(null);
    }
  };

  

  return (
    <div className="orders-table">
      <div className="orders__table__header">
        <h3>All in Stock</h3>

        <div className="stock__flex__search">
          <SearchComponent onSearch={handleSearch} />

          <button className="filter-button">
            <CreateNewFolderIcon />
            <p>Sheet Format</p>
          </button>

          <button onClick={() => setStockModalOpen(true)}>
            <AddIcon />
            <p>Add</p>
          </button>

          <button onClick={() => setFilterModalOpen(true)}>
            <FilterAltOutlinedIcon />
            <p>Filter</p>
          </button>
        </div>
      </div>

      <div className="table-s">
        <table>
          <thead>
            <tr>
              <th>PRODUCT NAME</th>
              <th>CATEGORY</th>
              <th>LEFT IN STOCK</th>
              <th>DESCRIPTION</th>
              <th>PRICE</th>
              <th>EDIT</th>
              <th>DELETE</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <NoItemFound message="No products in stock." />
                </td>
              </tr>
            ) : 
            

             loading ? (
              <tr>
                <td colSpan={5} style={{ textAlign: "center" }}>
                  Loading...
                </td>
              </tr>
            ) :
            
            
            (
              filteredProducts.map((p) => (
                <tr key={p.id}>
                  <td>
                    <div className="stock_product_img">
                      <img src={p.image} alt="product" />
                      <p>{p.name}</p>
                    </div>
                  </td>
                  <td>{p.segment?.name}</td>
                  <td>{p.stock_count}</td>
                  <td>{p.description.slice(0, 50)}...</td>
                  <td>${p.amount}</td>
                  <td>
                    <button
                      className="edit-button"
                      onClick={() => {
                        setEditModalOpen(true);
                        setActiveStock(p);
                      }}
                    >
                      <BorderColorIcon />
                    </button>
                  </td>
                  <td>
                    <button
                      className="delete-button"
                      onClick={() => {
                        setStockToDelete(p);
                        setDeleteModalOpen(true);
                      }}
                    >
                      delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="entries-control">
        <label>
          View
          <select value={entriesPerPage} onChange={handleEntriesChange}>
            <option value="2">2</option>
            <option value="5">5</option>
            <option value="10">10</option>
          </select>
          Per Page
        </label>

        <div className="right__pagination">
          <p>
            Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
            {Math.min(currentPage * entriesPerPage, totalProducts)} of {totalProducts} entries
          </p>

          <div className="pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                className={currentPage === page ? "active" : ""}
                onClick={() => handlePageChange(page)}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      <FilterStockModal
        isOpen={isFilterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        onFilter={handleFilter}
      />

      <StockAddModal
        isOpen={isStockModalOpen}
        onClose={() => setStockModalOpen(false)}
      />

      <EditStockModal
        isOpen={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        activeStock={activeStock}
      />

      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setStockToDelete(null);
        }}
        onConfirm={confirmDelete}
        productName={stockToDelete?.name || "this product"}
      />
    </div>
  );
};

export default StockTable;